import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {getHasTeammateBusyStatusFF} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownSeparator} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {AvailabilityStatusTypesEnum} from '../../../../store/data/teammates/availabilityStatusSelectors';
import {signoutUserAsyncAction} from '../../../../store/data/teammates/teammatesActions';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {MuteTeammateNotifications} from '../../../teammates/muteTeammateNotifications';
import {TeammateAvailabilityPicker} from '../../../teammates/teammateAvailabilityPicker';
import {UserProfileDropdownHeader} from './userProfileDropdownHeader';
import {UserProfileDropdownItem} from './userProfileDropdownItem';

/*
 * Props.
 */

interface UserProfileDropdownProps {
  popoverProps: PopoverTransferProps;
  teammate: TeammateModel;
  availabilityStatus: AvailabilityStatusTypesEnum;
  isConnected: boolean;
}

/*
 * Styles.
 */

const StyledDropdown = styled(Dropdown)`
  margin-top: 4px;
`;

const StyledMuteWrapperDiv = styled.div`
  margin: 0px 12px;
`;

const StyledAvailabilityDiv = styled.div`
  margin-left: -8px;
`;

/*
 * Component.
 */

export const UserProfileDropdown: FC<UserProfileDropdownProps> = ({
  popoverProps,
  teammate,
  availabilityStatus,
  isConnected,
}) => {
  const dispatch = useAppDispatch();
  const hasTeammateBusyStatusFF = useAppSelector(getHasTeammateBusyStatusFF);
  const onOpenChangelogClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.WHATS_NEW);

    return dispatch(navigateToLinkAction('https://community.front.com/product-updates'));
  };

  return (
    <StyledDropdown {...popoverProps} width={300}>
      <UserProfileDropdownHeader
        teammate={teammate}
        availabilityStatus={availabilityStatus}
        isConnected={isConnected}
      />

      {hasTeammateBusyStatusFF && (
        <>
          <DropdownSeparator />
          <StyledAvailabilityDiv>
            <TeammateAvailabilityPicker />
          </StyledAvailabilityDiv>
          <DropdownSeparator />
          <StyledMuteWrapperDiv>
            <MuteTeammateNotifications />
          </StyledMuteWrapperDiv>
        </>
      )}

      <DropdownList {...popoverProps}>
        <DropdownSeparator />
        <UserProfileDropdownItem onSelect={() => dispatch(navigateToLinkAction('/settings/profile'))}>
          <Trans id="user-profile-edit">Edit profile</Trans>
        </UserProfileDropdownItem>
        <UserProfileDropdownItem
          isExternal
          onSelect={onOpenChangelogClick}
          interactionId="user-profile-new-changelog"
        >
          <Trans id="user-profile-new-changelog">Product updates</Trans>
        </UserProfileDropdownItem>
        <UserProfileDropdownItem
          isExternal
          onSelect={() => dispatch(navigateToLinkAction('https://front.com/download'))}
        >
          <Trans id="user-profile-downloadmobileapps">Get the mobile app</Trans>
        </UserProfileDropdownItem>
        <UserProfileDropdownItem
          onSelect={() => dispatch(signoutUserAsyncAction({}))}
          isDisabled={!isConnected}
        >
          <Trans id="user-profile-new-signout">Sign out</Trans>
        </UserProfileDropdownItem>
      </DropdownList>
    </StyledDropdown>
  );
};
